import React, { useState, useEffect, useCallback } from 'react';
import { Formik } from 'formik';
import moment from 'moment';
import { DATA_TABLE_SOURCE_AUTOMATIC_ORDERS } from 'common/dataTableConstants';
import getNullEmptyValue from 'common/util/getNullEmptyValue';
import SrDataTableConnected from 'client/components/SrDataTableConnected';
import SrDataTableUI from 'client/components/SrDataTableUI';
import SrText from 'client/components/SrText';
import SrLink from 'client/components/SrLink/SrLink';
import { SR_TABLE_THEME_PARTNER_CLEAR } from 'client/components/SrTable';

const AUTO_UPDATE_INTERVAL = 15;

// Helper to format address
const formatAddress = (row) => {
  const parts = [];
  if (row.streetDisplay) {
    parts.push(row.streetDisplay);
  }
  if (row.unitNumber) {
    parts.push(`#${row.unitNumber}`);
  }
  if (row.city && row.stateCode) {
    parts.push(`${row.city} ${row.stateCode}`);
  }
  if (row.zip) {
    parts.push(row.zip);
  }

  const address = parts.join(', ');
  const name = [row.firstName, row.lastName].filter(Boolean).join(' ');

  const fullAddress = address && name ? `${address} - ${name}` : address || name || '--';
  const truncatedAddress =
    fullAddress.length > 60 ? `${fullAddress.substring(0, 60)}...` : fullAddress;

  return {
    full: fullAddress,
    truncated: truncatedAddress,
  };
};

// Helper to format plan
const formatPlan = (row) => {
  const price = row.variant_price || row.plan_price;
  if (!price && !row.internetSpeedMbps) return '--';

  const parts = [];
  if (price) parts.push(`$${price.toFixed(2)}`);
  if (row.internetSpeedMbps) parts.push(`${row.internetSpeedMbps}Mbps`);
  return parts.join(' - ');
};

// Helper to format datetime in user's timezone
const formatDateTime = (dateString) => {
  if (!dateString) return '--';
  return moment(dateString)
    .local() // Convert to local timezone
    .format('MMM D, YYYY h:mm A z'); // e.g., "Nov 11, 2024 3:30 PM EST"
};

const getStatusStyle = (status) => {
  switch (status?.toUpperCase()) {
    case 'FAILED':
      return { color: '#DC2626' }; // Red color for failed
    case 'COMPLETED':
      return { color: '#059669' }; // Green color for completed
    case 'AWAITING_RETRY':
      return { color: '#D97706' }; // Orange color for awaiting retry
    case 'IN_PROGRESS':
      return { color: '#2563EB' }; // Blue color for in progress
    case 'PENDING':
      return { color: '#6B7280' }; // Grey color for pending
    default:
      return { color: '#6B7280' };
  }
};

const getTypeImage = (type) => {
  switch (type?.toLowerCase()) {
    case 'cox':
      return (
        <img
          src="https://utility-profit.s3.amazonaws.com/partner-logos/cox.jpg"
          alt="Cox"
          style={{
            height: '25px',
            width: 'auto',
            objectFit: 'contain',
          }}
        />
      );
    case 'spectrum':
      return (
        <img
          src="https://utility-profit.s3.amazonaws.com/partner-logos/spectrum.jpg"
          alt="Spectrum"
          style={{
            height: '25px',
            width: 'auto',
            objectFit: 'contain',
          }}
        />
      );

    case 'att':
      return (
        <img
          src="https://utility-profit.s3.amazonaws.com/partner-logos/att.jpg"
          alt="AT&T"
          style={{
            height: '35px',
            width: 'auto',
            objectFit: 'contain',
          }}
        />
      );
    default:
      return type || '--';
  }
};

function createAutomaticOrderRows({ rows }) {
  return rows.map((row) => ({
    cells: [
      {
        dataTableFieldId: 'alpha_id',
        label: 'ID',
        value: row.id || '--',
      },
      {
        dataTableFieldId: 'type',
        label: 'Type',
        value: row.type || '--',
        content: getTypeImage(row.type),
      },
      {
        dataTableFieldId: 'activation',
        label: 'Activation',
        value: formatAddress(row).truncated,
        content: (
          <span title={formatAddress(row).full} style={{ cursor: 'default' }}>
            {formatAddress(row).truncated}
          </span>
        ),
      },
      {
        dataTableFieldId: 'plan',
        label: 'Plan',
        value: formatPlan(row),
      },
      {
        dataTableFieldId: 'status',
        label: 'Status',
        value: row.status || '--',
        content: (
          <SrText font="greycliff" weight="700" style={getStatusStyle(row.status)}>
            {row.status || '--'}
          </SrText>
        ),
      },
      {
        dataTableFieldId: 'attempts',
        label: 'Attempts',
        value: row.attempts_count || '--',
      },
      {
        dataTableFieldId: 'confirmation_number',
        label: 'Confirmation Number',
        value: row.confirmation_number || '--',
      },
      {
        dataTableFieldId: 'created_at',
        label: 'Created At',
        value: formatDateTime(row.created_at),
      },
      {
        label: 'View details',
        content: (
          <SrLink to={`/utility-profit/admin/automatic-orders/${row.id}`}>
            <SrText
              font="greycliff"
              color="blue300"
              cursor="pointer"
              weight="800"
              fontSize="16px"
              lineHeight="50px"
            >
              View details
            </SrText>
          </SrLink>
        ),
      },
    ].map((cell) => ({
      ...cell,
      content: cell.content || getNullEmptyValue(cell.value, '--'),
    })),
  }));
}

const tableHeaders = [
  { dataTableFieldId: 'id', content: 'ID' },
  { dataTableFieldId: 'type', content: 'Type' },
  { dataTableFieldId: 'activation', content: 'Activation' },
  { dataTableFieldId: 'plan', content: 'Plan' },
  { dataTableFieldId: 'status', content: 'Status' },
  { dataTableFieldId: 'attempts', content: 'Attempts' },
  { dataTableFieldId: 'confirmation_number', content: 'Confirmation Number' },
  { dataTableFieldId: 'created_at', content: 'Created At' },
  { dataTableFieldId: 'actions', content: 'Actions' },
];

const AutoUpdateToggle = ({ enabled, onToggle, countdown }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      gap: '10px',
      marginBottom: '15px',
    }}
  >
    <SrText
      onClick={() => onToggle(!enabled)}
      cursor="pointer"
      color={enabled ? 'blue300' : 'gray500'}
      weight="600"
    >
      {enabled ? 'Auto update on' : 'Auto update off'}
    </SrText>
    {enabled && (
      <SrText color="gray500" fontSize="14px">
        Updating in {countdown} seconds
      </SrText>
    )}
  </div>
);

const UPAdminAutomaticOrders = () => {
  const [autoUpdateEnabled, setAutoUpdateEnabled] = useState(true);
  const [countdown, setCountdown] = useState(AUTO_UPDATE_INTERVAL);
  const [updateKey, setUpdateKey] = useState(0);

  const handleUpdate = useCallback(() => {
    setUpdateKey((prev) => prev + 1);
    setCountdown(AUTO_UPDATE_INTERVAL);
  }, []);

  useEffect(() => {
    let timer;
    if (autoUpdateEnabled) {
      timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            handleUpdate();
            return AUTO_UPDATE_INTERVAL;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [autoUpdateEnabled, handleUpdate]);

  return (
    <div>
      <AutoUpdateToggle
        enabled={autoUpdateEnabled}
        onToggle={setAutoUpdateEnabled}
        countdown={countdown}
      />
      <Formik
        initialValues={{
          tableTheme: SR_TABLE_THEME_PARTNER_CLEAR,
        }}
      >
        {(formikBag) => (
          <SrDataTableConnected
            key={updateKey}
            dataSource={DATA_TABLE_SOURCE_AUTOMATIC_ORDERS}
            initialViewSettings={{
              sort: {
                created_at: 'desc',
              },
            }}
            count={20}
            itemsPropName="rows"
            resetOnUnmount
          >
            {({ rows, viewSettings, dataTableApiState, onInfiniteScrollWrapperRef }) => {
              const processedRows = createAutomaticOrderRows({ rows });
              return (
                <div ref={onInfiniteScrollWrapperRef}>
                  <SrDataTableUI
                    theme={formikBag.values.tableTheme}
                    className="tableAccountDashboard"
                    dataTableViewSettings={viewSettings}
                    headers={tableHeaders}
                    rows={processedRows}
                    sticky
                  />
                  {dataTableApiState.inProgress && <div className="loading-ellipsis" />}
                </div>
              );
            }}
          </SrDataTableConnected>
        )}
      </Formik>
    </div>
  );
};

export default UPAdminAutomaticOrders;
